import type { SupportCenterAppointmentChannel } from './supportCenterAppointments'
import type {
  SupportCenterAppointmentStatus,
  SupportCenterFormKind,
  SupportCenterTriageStatus,
} from './supportCenterTriage'

export type { SupportCenterAppointmentChannel }

export type SupportCenterDashboardTab = 'volumetria' | 'historico'

export type SupportCenterTriageStatusFilter = SupportCenterTriageStatus | 'none'

export type SupportCenterVolumeFilters = {
  date_from?: string
  date_to?: string
  specialization_id?: string
  /** Escopo do usuário (profissionais vinculados) */
  specialization_ids?: string[]
  professional_user_id?: string
  professional_user_ids?: string[]
  /** Recorte da agenda: e-mails em internal_emails */
  attendant_email?: string
  appointment_statuses?: SupportCenterAppointmentStatus[]
  triage_statuses?: SupportCenterTriageStatusFilter[]
  /** Canais de agendamento. Vazio = todos. */
  channels?: SupportCenterAppointmentChannel[]
  patient_query?: string
  /** Números da apólice (policy_code). Vazio = todos. */
  policy_numbers?: string[]
  /** Códigos do subgrupo (agreement_code). Vazio = todos. */
  convenio_codes?: string[]
  /** Coberturas (support_center_assistances) vinculadas ao agendamento. */
  assistance_ids?: string[]
  /** Origens do encaminhamento (triagem). Vazio = todas. */
  referral_origin_ids?: string[]
  /** Tipos de demanda (triagem). Vazio = todos. */
  demand_type_ids?: string[]
}

export type SupportCenterDashboardFilters = {
  dateFrom: string
  dateTo: string
  specializationIds: string[]
  professionalUserIds: string[]
  appointmentStatuses: SupportCenterAppointmentStatus[]
  triageStatuses: SupportCenterTriageStatusFilter[]
  /** Canais de agendamento selecionados. Vazio = todos. */
  channels: SupportCenterAppointmentChannel[]
  patientQuery: string
  /** Números da apólice selecionados. Vazio = todos. */
  policyNumbers: string[]
  /** Códigos do convênio selecionados. Vazio = todos. */
  convenioCodes: string[]
  /** IDs das coberturas (assistências) selecionadas. Vazio = todas. */
  assistanceIds: string[]
  /** Origens do encaminhamento selecionadas. Vazio = todas. */
  referralOriginIds: string[]
  /** Tipos de demanda selecionados. Vazio = todos. */
  demandTypeIds: string[]
  /** Filtro opcional por e-mail em internal_emails (usado quando aplicável) */
  attendantEmail?: string
}

export type SupportCenterAppointmentListFilters = {
  date_from?: string
  date_to?: string
  specialization_id?: string
  specialization_ids?: string[]
  professional_user_id?: string
  professional_user_ids?: string[]
  appointment_statuses?: SupportCenterAppointmentStatus[]
  triage_statuses?: SupportCenterTriageStatusFilter[]
  /** Canais de agendamento. Vazio = todos. */
  channels?: SupportCenterAppointmentChannel[]
  attendant_email?: string
  /** CPF (parcial/completo) ou nome do paciente */
  patient_query?: string
  /** Números da apólice (policy_code). Vazio = todos. */
  policy_numbers?: string[]
  /** Códigos do subgrupo (agreement_code). Vazio = todos. */
  convenio_codes?: string[]
  /** Coberturas (support_center_assistances) vinculadas ao agendamento. */
  assistance_ids?: string[]
  /** Origens do encaminhamento (triagem). Vazio = todas. */
  referral_origin_ids?: string[]
  /** Tipos de demanda (triagem). Vazio = todos. */
  demand_type_ids?: string[]
}

export type SupportCenterVolumeByStatus = Record<SupportCenterAppointmentStatus, number>

export type SupportCenterVolumeBySpecialization = {
  specialization_id: string
  name: string
  total: number
}

export type SupportCenterVolumeByDay = {
  date: string
  total: number
}

export type SupportCenterVolumeByKinship = {
  name: string
  total: number
}

export type SupportCenterVolumeByChannel = {
  channel: SupportCenterAppointmentChannel
  name: string
  total: number
}

/** Agregação por item de catálogo da triagem (origem / tipo de demanda). */
export type SupportCenterVolumeByCatalog = {
  id: string
  name: string
  total: number
}

export type SupportCenterTriageVolumeByStatus = Record<SupportCenterTriageStatus, number>

export type SupportCenterVolumeMetrics = {
  total: number
  by_status: SupportCenterVolumeByStatus
  cancel_rate: number
  by_specialization: SupportCenterVolumeBySpecialization[]
  by_day: SupportCenterVolumeByDay[]
  by_kinship: SupportCenterVolumeByKinship[]
  by_channel: SupportCenterVolumeByChannel[]
  triage_by_status: SupportCenterTriageVolumeByStatus
  triage_by_specialization: SupportCenterVolumeBySpecialization[]
  triage_by_day: SupportCenterVolumeByDay[]
  triage_by_referral_origin: SupportCenterVolumeByCatalog[]
  triage_by_demand_type: SupportCenterVolumeByCatalog[]
}

/** Registro completo de support_center_appointments, com FKs resolvidas para exibição. */
export type SupportCenterAppointmentRecord = {
  id: string
  scheduled_at: string
  status: SupportCenterAppointmentStatus
  /** Canal em que o agendamento foi feito (coluna channel). */
  channel: string | null
  holder_cpf: string
  holder_name: string | null
  patient_cpf: string
  patient_name: string | null
  relationship_to_holder: string | null
  specialty_id: string
  specialization_name: string | null
  assistance_id: string | null
  assistance_name: string | null
  assistance_code: string | null
  professional_user_id: string | null
  professional_name: string | null
  internal_emails: string[]
  phone_contact_id: string | null
  patient_phone: string | null
  email_contact_ids: string[]
  client_emails: string[]
  call_link: string | null
  /** URL encurtada da reunião (coluna call_link_short). */
  call_link_short: string | null
  outlook_event_id: string | null
  triage_id: string | null
  triage_status: SupportCenterTriageStatus | null
  confirmed_at: string | null
  confirmation_requested_at: string | null
  attendance_started_at: string | null
  attendance_completed_at: string | null
  policy_code: number | null
  agreement_code: number | null
  assistance_provider_code: number | null
  cancel_reason_id: string | null
  cancel_reason_name: string | null
  cancellation_channel: string | null
  deleted_at: string | null
  deleted_by: string | null
  deleted_by_name: string | null
  created_at: string
  updated_at: string
}

export type SupportCenterHistoryItem = {
  appointment_id: string
  scheduled_at: string
  status: SupportCenterAppointmentStatus
  holder_cpf: string
  patient_cpf: string
  patient_phone: string | null
  client_email: string | null
  holder_name: string | null
  patient_name: string | null
  relationship_to_holder: string | null
  specialization_id: string
  specialization_name: string | null
  form_kind: SupportCenterFormKind | null
  triage_id: string | null
  triage_status: SupportCenterTriageStatus | null
  attendant_email: string | null
  attendant_name: string | null
}

export const APPOINTMENT_STATUS_LABELS: Record<SupportCenterAppointmentStatus, string> = {
  marcado: 'Marcado',
  reagendado: 'Reagendado',
  cancelado: 'Cancelado',
  realizado: 'Realizado',
  nao_realizado: 'Não Realizado',
}
